// MyAttendance.jsx - WITH SKELETON LOADING & SPECIFIC CLASSNAMES
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMyAttendance } from '../features/attendance/attendanceThunks';
import {
  selectMyAttendance,
  selectMyAttendanceLoading,
  selectMyAttendanceError,
} from '../features/attendance/attendanceSlice';
import '../CSS/MyAttendance.css';
import Sidebar from './Sidebar';

// Skeleton Components
const SkeletonStatCard = () => (
  <div className="my-att-stat-card">
    <div className="my-att-stat-info">
      <div className="my-att-skeleton-stat-value"></div>
      <div className="my-att-skeleton-stat-label"></div>
    </div>
  </div>
);

const SkeletonCalendarDay = () => (
  <div className="my-att-calendar-day my-att-skeleton-day">
    <div className="my-att-skeleton-day-number"></div>
    <div className="my-att-skeleton-day-status"></div>
  </div>
);

const SkeletonTableRow = () => (
  <tr className="my-att-skeleton-row">
    <td><div className="my-att-skeleton-cell"></div></td>
    <td><div className="my-att-skeleton-cell"></div></td>
    <td><div className="my-att-skeleton-cell"></div></td>
    <td><div className="my-att-skeleton-cell"></div></td>
    <td><div className="my-att-skeleton-cell"></div></td>
    <td><div className="my-att-skeleton-cell"></div></td>
  </tr>
);

const MyAttendance = () => {
  const dispatch = useDispatch();
  const myAttendance = useSelector(selectMyAttendance);
  const loading = useSelector(selectMyAttendanceLoading);
  const error = useSelector(selectMyAttendanceError);

  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [view, setView] = useState('calendar'); // 'calendar' or 'table'

  // Fetch data on mount and when filters change
  useEffect(() => {
    dispatch(fetchMyAttendance({
      month: selectedMonth,
      year: selectedYear,
    }));
  }, [dispatch, selectedMonth, selectedYear]);

  // Generate years array
  const years = Array.from(
    { length: 5 },
    (_, i) => new Date().getFullYear() - 2 + i
  );

  // Get month name
  const getMonthName = (month) => {
    const date = new Date(2000, month - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'long' });
  };

  // Get days in month
  const getDaysInMonth = (month, year) => {
    return new Date(year, month, 0).getDate();
  };

  // Get first day of month (0 = Sunday)
  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month - 1, 1).getDay();
  };

  // Find record for a specific date
  const getRecordForDate = (date) => {
    const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(date).padStart(2, '0')}`;
    return myAttendance.records.find(r => r.date === dateStr);
  };

  // Format time
  const formatTime = (datetime) => {
    if (!datetime) return '--:--';
    const date = new Date(datetime);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  // Get status color
  const getStatusColor = (status) => {
    const colorMap = {
      'PRESENT': '#4caf50',
      'LATE': '#ff9800',
      'ABSENT': '#f44336',
      'HALF_DAY': '#00bcd4',
      'LEAVE': '#9c27b0',
      'MEDICAL_LEAVE': '#2196f3',
    };
    return colorMap[status] || '#9e9e9e';
  };

  // Get status abbreviation
  const getStatusAbbr = (status) => {
    const abbrMap = {
      'PRESENT': 'P',
      'LATE': 'L',
      'ABSENT': 'A',
      'HALF_DAY': 'H',
      'LEAVE': 'LV',
      'MEDICAL_LEAVE': 'ML',
    };
    return abbrMap[status] || '?';
  };

  // Calendar view
  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
    const firstDay = getFirstDayOfMonth(selectedMonth, selectedYear);
    const days = [];

    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="my-att-calendar-day my-att-empty"></div>);
    }

    // Days of the month
    for (let date = 1; date <= daysInMonth; date++) {
      const record = getRecordForDate(date);
      const isToday = 
        date === new Date().getDate() &&
        selectedMonth === new Date().getMonth() + 1 &&
        selectedYear === new Date().getFullYear();

      days.push(
        <div
          key={date}
          className={`my-att-calendar-day ${isToday ? 'my-att-today' : ''} ${record ? 'my-att-has-record' : ''}`}
          style={{ borderColor: record ? getStatusColor(record.status) : '#e0e0e0' }}
        >
          <div className="my-att-day-number">{date}</div>
          {record && (
            <div className="my-att-day-status" style={{ background: getStatusColor(record.status) }}>
              <span className="my-att-status-abbr">{getStatusAbbr(record.status)}</span>
              {record.work_hours && (
                <span className="my-att-hours-text">{record.work_hours.toFixed(1)}h</span>
              )}
            </div>
          )}
        </div>
      );
    }

    return days;
  };

  if (loading && !myAttendance.records.length) {
    return (
      <div className='index-box'>
        <Sidebar />
        <div className="my-att-my-attendance">
          {/* Skeleton Header */}
          <div className="my-att-attendance-header">
            <div className="my-att-header-left">
              <div className="my-att-skeleton-header-title"></div>
              <div className="my-att-skeleton-header-subtitle"></div>
            </div>
            <div className="my-att-header-controls">
              <div className="my-att-skeleton-select"></div>
              <div className="my-att-skeleton-select"></div>
              <div className="my-att-skeleton-toggle"></div>
            </div>
          </div>

          {/* Skeleton Summary Stats */}
          <div className="my-att-summary-stats">
            {[...Array(5)].map((_, i) => <SkeletonStatCard key={i} />)}
          </div>

          {/* Skeleton Calendar */}
          <div className="my-att-calendar-container">
            <div className="my-att-calendar-header">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="my-att-weekday">{day}</div>
              ))}
            </div>
            <div className="my-att-calendar-grid">
              {[...Array(35)].map((_, i) => <SkeletonCalendarDay key={i} />)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='index-box'>
        <Sidebar />
        <div className="my-att-my-attendance">
          <div className="my-att-error-container">
            <p className="my-att-error-message">{error}</p>
            <button onClick={() => dispatch(fetchMyAttendance({ month: selectedMonth, year: selectedYear }))} className="my-att-btn-retry">
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='index-box'>
      <Sidebar />
      <div className="my-att-my-attendance">
        {/* Header */}
        <div className="my-att-attendance-header">
          <div className="my-att-header-left">
            <h1>My Attendance</h1>
            <p className="my-att-employee-info">
              {myAttendance.employeeName} • {myAttendance.role} • {myAttendance.department}
            </p>
          </div>
          <div className="my-att-header-controls">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
              className="my-att-month-select"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                <option key={month} value={month}>
                  {getMonthName(month)}
                </option>
              ))}
            </select>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="my-att-year-select"
            >
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            <div className="my-att-view-toggle">
              <button
                className={view === 'calendar' ? 'my-att-active' : ''}
                onClick={() => setView('calendar')}
              >
                Calendar
              </button>
              <button
                className={view === 'table' ? 'my-att-active' : ''}
                onClick={() => setView('table')}
              >
                Table
              </button>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="my-att-summary-stats">
          <div className="my-att-stat-card my-att-stat-present">
            <div className="my-att-stat-info">
              <div className="my-att-stat-value">{myAttendance.summary.present}</div>
              <div className="my-att-stat-label">Present</div>
            </div>
          </div>
          <div className="my-att-stat-card my-att-stat-late">
            <div className="my-att-stat-info">
              <div className="my-att-stat-value">{myAttendance.summary.late}</div>
              <div className="my-att-stat-label">Late</div>
            </div>
          </div>
          <div className="my-att-stat-card my-att-stat-absent">
            <div className="my-att-stat-info">
              <div className="my-att-stat-value">{myAttendance.summary.absent}</div>
              <div className="my-att-stat-label">Absent</div>
            </div>
          </div>
          <div className="my-att-stat-card my-att-stat-leave">
            <div className="my-att-stat-info">
              <div className="my-att-stat-value">{myAttendance.summary.onLeave}</div>
              <div className="my-att-stat-label">Leave</div>
            </div>
          </div>
          <div className="my-att-stat-card my-att-stat-hours">
            <div className="my-att-stat-info">
              <div className="my-att-stat-value">{myAttendance.summary.avgWorkHours.toFixed(1)}</div>
              <div className="my-att-stat-label">Avg Hours/Day</div>
            </div>
          </div>
        </div>

        {/* Calendar or Table View */}
        {view === 'calendar' ? (
          <div className="my-att-calendar-container">
            <div className="my-att-calendar-header">
              <div className="my-att-weekday">Sun</div>
              <div className="my-att-weekday">Mon</div>
              <div className="my-att-weekday">Tue</div>
              <div className="my-att-weekday">Wed</div>
              <div className="my-att-weekday">Thu</div>
              <div className="my-att-weekday">Fri</div>
              <div className="my-att-weekday">Sat</div>
            </div>
            <div className="my-att-calendar-grid">
              {renderCalendar()}
            </div>
          </div>
        ) : (
          <div className="my-att-table-container">
            <table className="my-att-attendance-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Day</th>
                  <th>Check In</th>
                  <th>Check Out</th>
                  <th>Work Hours</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {myAttendance.records.length > 0 ? (
                  myAttendance.records.map((record) => (
                    <tr key={record.id}>
                      <td>{new Date(record.date).toLocaleDateString()}</td>
                      <td>{new Date(record.date).toLocaleDateString('en-US', { weekday: 'short' })}</td>
                      <td>{formatTime(record.punch_in)}</td>
                      <td>{formatTime(record.punch_out)}</td>
                      <td>{record.work_hours ? `${record.work_hours.toFixed(2)} hrs` : '--'}</td>
                      <td>
                        <span className="my-att-status-badge" style={{ background: getStatusColor(record.status), color: 'white' }}>
                          {record.status.replace('_', ' ')}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="my-att-no-data">
                      No attendance records for this month
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Legend */}
        <div className="my-att-legend">
          <h3>Status Legend</h3>
          <div className="my-att-legend-items">
            <div className="my-att-legend-item">
              <span className="my-att-legend-color" style={{ background: '#4caf50' }}></span>
              <span>P - Present</span>
            </div>
            <div className="my-att-legend-item">
              <span className="my-att-legend-color" style={{ background: '#ff9800' }}></span>
              <span>L - Late</span>
            </div>
            <div className="my-att-legend-item">
              <span className="my-att-legend-color" style={{ background: '#f44336' }}></span>
              <span>A - Absent</span>
            </div>
            <div className="my-att-legend-item">
              <span className="my-att-legend-color" style={{ background: '#00bcd4' }}></span>
              <span>H - Half Day</span>
            </div>
            <div className="my-att-legend-item">
              <span className="my-att-legend-color" style={{ background: '#9c27b0' }}></span>
              <span>LV - Leave</span>
            </div>
            <div className="my-att-legend-item">
              <span className="my-att-legend-color" style={{ background: '#2196f3' }}></span>
              <span>ML - Medical Leave</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyAttendance;
